import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronsUpDown, X } from "lucide-react";
import {
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { filterBySearchTerms } from "@/lib/search-terms";
import { cn } from "@/lib/utils";

export interface SelectOption {
  value: string;
  label: string;
  /** Secondary line rendered under the label, e.g. a contact name. */
  description?: string;
  /** Extra text matched by the search filter in addition to label/value. */
  searchText?: string;
  disabled?: boolean;
}

/**
 * Party lists run to hundreds of rows, and rendering them all locks the browser
 * for seconds. Shorter lists stay eager so their layout is untouched.
 */
const VIRTUALIZE_ABOVE = 80;
const ESTIMATED_ROW_HEIGHT = 32;
const ESTIMATED_TWO_LINE_ROW_HEIGHT = 50;
/** Row heights arrive asynchronously, so give a long jump time to land. */
const SCROLL_SETTLE_MS = 400;

export interface SelectProps {
  options: SelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Applied to the trigger so a <Label htmlFor> can point at it. */
  triggerId?: string;
  /**
   * Overrides the trigger text. Needed where the saved answer predates the
   * option list and so matches no option value.
   */
  triggerLabel?: string;
  buttonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "usernav"
    | "accent";
  buttonSize?: "default" | "sm" | "xs" | "lg" | "icon";
  // Multi-select props
  multiple?: boolean;
  showBadges?: boolean;
  clearAllText?: string;
}

export function Select({
  options,
  value,
  onValueChange,
  placeholder = "Select option",
  searchPlaceholder = "Search...",
  emptyMessage = "No option found.",
  disabled = false,
  className,
  triggerClassName,
  contentClassName,
  triggerId,
  triggerLabel,
  buttonVariant = "outline",
  buttonSize = "default",
  multiple = false,
  showBadges = false,
  clearAllText = "Clear all",
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState<string | null>(null);
  // The list mounts into a portal a commit later than this component, so the
  // virtualizer only finds its scroll container if mounting re-renders here.
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);

  /**
   * A value repeated across options (the same party attached to a question more
   * than once) collides on both the React key and cmdk's value lookup, which
   * leaves stale rows behind in the list after every filter.
   */
  const uniqueOptions = useMemo(() => {
    const seen = new Set<string>();
    return options.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [options]);

  /**
   * Closer matches are floated to the top; the sort is stable, so caller order
   * is preserved within a rank and cmdk's own score-based re-sort stays off.
   */
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return uniqueOptions;
    const rankOf = (label: string) => {
      if (label.startsWith(query)) return 0;
      if (label.includes(query)) return 1;
      return 2;
    };
    return filterBySearchTerms(uniqueOptions, query, (option) =>
      [option.label, option.description, option.searchText]
        .filter(Boolean)
        .join(" "),
    )
      .map((option) => ({ option, rank: rankOf(option.label.toLowerCase()) }))
      .sort((a, b) => a.rank - b.rank)
      .map((match) => match.option);
  }, [uniqueOptions, search]);

  const selectedOptions = uniqueOptions.filter((option) =>
    value.includes(option.value),
  );
  const displayText = multiple
    ? value.length > 0
      ? `${value.length} selected`
      : placeholder
    : triggerLabel?.trim() || selectedOptions[0]?.label || placeholder;

  const isVirtual = filteredOptions.length > VIRTUALIZE_ABOVE;
  const hasDescriptions = uniqueOptions.some((option) => option.description);
  const virtualizer = useVirtualizer({
    count: isVirtual ? filteredOptions.length : 0,
    getScrollElement: () => listElement,
    estimateSize: () =>
      hasDescriptions ? ESTIMATED_TWO_LINE_ROW_HEIGHT : ESTIMATED_ROW_HEIGHT,
    overscan: 10,
  });

  const highlightedIndex = Math.max(
    0,
    filteredOptions.findIndex((option) => option.value === highlighted),
  );
  // Key repeat fires several times before React re-renders, so each step reads
  // the position from here rather than from the not-yet-applied state.
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  // Scrolling after the render keeps a burst of key repeats from each
  // scrolling off a stale offset and overshooting the final row.
  const pendingScrollRef = useRef(false);
  useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    // A long jump lands on estimated heights, so it is repeated as the newly
    // rendered rows report real ones. Once the row exists the browser places
    // it, which accounts for the list's padding as the estimates cannot.
    virtualizer.scrollToIndex(highlightedIndex);
    let frame = 0;
    const deadline = performance.now() + SCROLL_SETTLE_MS;
    const settle = () => {
      const row = listElement?.querySelector(
        `[data-index="${highlightedIndex}"]`,
      );
      if (row && listElement) {
        row.scrollIntoView({ block: "nearest" });
        const rowBox = row.getBoundingClientRect();
        const listBox = listElement.getBoundingClientRect();
        if (rowBox.top >= listBox.top && rowBox.bottom <= listBox.bottom) {
          return;
        }
      } else {
        virtualizer.scrollToIndex(highlightedIndex);
      }
      if (performance.now() < deadline) frame = requestAnimationFrame(settle);
    };
    frame = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(frame);
  }, [highlightedIndex, virtualizer, listElement]);

  const handleSelect = (selectedValue: string) => {
    if (multiple) {
      const newValues = value.includes(selectedValue)
        ? value.filter((v) => v !== selectedValue)
        : [...value, selectedValue];
      onValueChange(newValues);
    } else {
      onValueChange([selectedValue]);
      setOpen(false);
      // Closing here bypasses onOpenChange, so the filter has to be reset
      // explicitly or the next open is still narrowed by the old search.
      setSearch("");
      setHighlighted(null);
    }
  };

  /**
   * cmdk only walks rows that are mounted, so once the list is virtualized it
   * can no longer drive the keyboard. It hands control over whenever the event
   * comes back already defaultPrevented.
   */
  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isVirtual) return;
    const step = (from: number, direction: 1 | -1) => {
      for (
        let index = from + direction;
        index >= 0 && index < filteredOptions.length;
        index += direction
      ) {
        if (!filteredOptions[index].disabled) return index;
      }
      return from;
    };
    const moveTo = (index: number) => {
      const option = filteredOptions[index];
      if (!option) return;
      event.preventDefault();
      highlightedIndexRef.current = index;
      setHighlighted(option.value);
      pendingScrollRef.current = true;
    };

    switch (event.key) {
      case "ArrowDown":
        moveTo(step(highlightedIndexRef.current, 1));
        break;
      case "ArrowUp":
        moveTo(step(highlightedIndexRef.current, -1));
        break;
      case "Home":
        moveTo(0);
        break;
      case "End":
        moveTo(filteredOptions.length - 1);
        break;
      case "Enter": {
        const option = filteredOptions[highlightedIndexRef.current];
        if (!option || option.disabled) break;
        event.preventDefault();
        handleSelect(option.value);
        break;
      }
    }
  };

  const handleClearAll = () => {
    onValueChange([]);
  };

  const handleRemoveValue = (valueToRemove: string) => {
    onValueChange(value.filter((v) => v !== valueToRemove));
  };

  const renderOption = (option: SelectOption) => (
    <CommandItem
      key={option.value}
      value={option.value}
      keywords={[
        option.label,
        option.description ?? "",
        option.searchText ?? "",
      ]}
      disabled={option.disabled}
      onSelect={() => handleSelect(option.value)}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          value.includes(option.value) ? "opacity-100" : "opacity-0",
        )}
      />
      {option.description ? (
        <span className="flex flex-col gap-0.5">
          <span>{option.label}</span>
          <span className="text-xs text-muted-foreground">
            {option.description}
          </span>
        </span>
      ) : (
        option.label
      )}
    </CommandItem>
  );

  // Portal into the inspection portal root so jurisdiction theme CSS vars
  // (--primary / --ring) apply to dropdown selected/hover states.
  const portalContainer =
    typeof document !== "undefined"
      ? ((document.querySelector(".inspection-portal") as HTMLElement | null) ??
        undefined)
      : undefined;

  return (
    <div className="space-y-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setSearch("");
            setHighlighted(null);
          }
        }}
        modal={false}
      >
        <PopoverTrigger asChild className={triggerClassName}>
          <Button
            id={triggerId}
            variant={buttonVariant}
            size={buttonSize}
            disabled={disabled}
            // biome-ignore lint/a11y/useSemanticElements: custom combobox uses Button as trigger per design
            role="combobox"
            aria-expanded={open}
            aria-label={displayText}
            className={cn(
              "w-full justify-between",
              displayText === placeholder && "text-muted-foreground",
              className,
            )}
          >
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          container={portalContainer}
          className={cn("w-full p-0", contentClassName)}
        >
          <Command
            shouldFilter={false}
            className="flex flex-col"
            // cmdk keeps its highlighted row across query changes and scrolls it
            // back into view, which leaves a new result set opened part-way down.
            // Clearing the highlight falls back to the first row of the results.
            value={highlighted ?? filteredOptions[0]?.value ?? ""}
            onValueChange={setHighlighted}
            onKeyDown={handleListKeyDown}
          >
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={(next) => {
                setSearch(next);
                setHighlighted(null);
                if (listElement) listElement.scrollTop = 0;
              }}
            />
            <CommandList
              ref={setListElement}
              className="max-h-[min(20rem,60vh)]"
            >
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              ) : isVirtual ? (
                <CommandGroup>
                  <div
                    className="relative w-full"
                    style={{ height: virtualizer.getTotalSize() }}
                  >
                    {virtualizer.getVirtualItems().map((row) => (
                      <div
                        key={filteredOptions[row.index].value}
                        data-index={row.index}
                        ref={virtualizer.measureElement}
                        className="absolute left-0 top-0 w-full"
                        style={{ transform: `translateY(${row.start}px)` }}
                      >
                        {renderOption(filteredOptions[row.index])}
                      </div>
                    ))}
                  </div>
                </CommandGroup>
              ) : (
                <CommandGroup>{filteredOptions.map(renderOption)}</CommandGroup>
              )}
            </CommandList>
            {multiple && value.length > 0 && (
              <div className="border-t px-2 flex-shrink-0">
                <Button
                  variant="link"
                  size="xs"
                  onClick={handleClearAll}
                  className="w-full justify-start text-xs p-0 underline"
                >
                  {clearAllText}
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {multiple && showBadges && value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <Badge key={option.value} variant="default">
              {option.label}
              <button
                type="button"
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={() => handleRemoveValue(option.value)}
                aria-label={`Remove ${option.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
